import { useCallback, useEffect, useRef } from "react";
import useUndo from "use-undo";
import { reducer } from "./reducer.js";

/**
 * Combines our data reducer with use-undo's history management.
 *
 * - Data actions (INITIALIZE, UPDATE_ROWS, UPDATE_STATUS_BULK) go through
 *   the reducer to compute new rows, then push to use-undo history via set().
 * - UNDO / REDO dispatch straight to use-undo's undo() / redo().
 * - INITIALIZE uses reset() so it clears history (fresh load = no undo steps).
 */
export function useUndoRedo() {
  const [rowsState, { set, reset, undo, redo, canUndo, canRedo }] =
    useUndo([]);

  const rowsRef = useRef(rowsState.present);
  useEffect(() => {
    rowsRef.current = rowsState.present;
  }, [rowsState.present]);

  // Keep a stable ref to the latest use-undo actions so dispatch never changes
  // its own reference (which would re-trigger the INITIALIZE effect in the parent).
  const actionsRef = useRef({ set, reset, undo, redo });
  useEffect(() => {
    actionsRef.current = { set, reset, undo, redo };
  }, [set, reset, undo, redo]);

  const dispatch = useCallback((action) => {
    const { set: s, reset: r, undo: u, redo: re } = actionsRef.current;
    if (action.type === "UNDO") { u(); return; }
    if (action.type === "REDO") { re(); return; }

    const nextRows = reducer(rowsRef.current, action);

    if (action.type === "INITIALIZE") r(nextRows);
    else s(nextRows);
  }, []); // stable — reads latest values from refs

  return {
    rows: rowsState.present,
    dispatch,
    canUndo,
    canRedo,
  };
}
